import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { MasterCompanyStats } from "../../../api/client.ts";
import FunnelBars from "../../charts/FunnelBars.tsx";
import { formatNumber, percent, percentOf } from "../../../utils/format.ts";
import CountryName from "../../CountryName.tsx";
import Panel from "../../Panel.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";

/** From a registered company to a classified one, with what each step added today. */
export function EnrichmentFunnelPanel({ stats }: { stats: MasterCompanyStats }) {
  const added = [
    { label: "companies", value: stats.companies_added_24h },
    { label: "websites found", value: stats.websites_found_24h },
    { label: "crawled", value: stats.websites_crawled_24h },
    { label: "classified", value: stats.companies_classified_24h },
    { label: "tech scanned", value: stats.technologies_scanned_24h },
    { label: "LinkedIn pages", value: stats.linkedin_urls_found_24h },
  ].filter((a) => a.value > 0);

  return (
    <Panel title="Enrichment funnel" subtitle="Every master company, from register to classification" fill>
      <Stack spacing={2}>
        <FunnelBars
          stages={[
            { label: "Companies", value: stats.total_companies },
            { label: "Have a website", value: stats.companies_with_website },
            { label: "Crawled", value: stats.websites_crawled },
            { label: "Classified", value: stats.companies_classified },
          ]}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Side
            label="Technology scanned"
            value={stats.technologies_scanned}
            caption={`${percent(stats.technologies_scanned, stats.companies_with_website)} of companies with a website`}
          />
          <Side
            label="LinkedIn pages"
            value={stats.linkedin_urls_found}
            caption={`${percent(stats.linkedin_urls_found, stats.total_companies)} of companies`}
          />
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {added.length
            ? `Last 24 hours: ${added.map((a) => `${formatNumber(a.value)} ${a.label}`).join(" · ")}`
            : "Nothing new in the last 24 hours"}
        </Typography>
      </Stack>
    </Panel>
  );
}

function Side({ label, value, caption }: { label: string; value: number; caption: string }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="h6" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatNumber(value)}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {caption}
      </Typography>
    </Box>
  );
}

const MIN_COUNTRY_COMPANIES = 10;

/** Per country: companies and how far down the funnel they got, as shares. */
export function CountryCoveragePanel({ stats }: { stats: MasterCompanyStats }) {
  const countries = stats.companies_by_country.filter((c) => c.count >= MIN_COUNTRY_COMPANIES);
  return (
    // From md up the panel sits absolutely in its grid cell, so the row takes the
    // funnel panel's height and the table scrolls inside it.
    <Panel
      title="Coverage by country"
      count={countries.length}
      disablePadding
      fill
      sx={{
        display: "flex",
        flexDirection: "column",
        position: { md: "absolute" },
        inset: { md: 0 },
        maxHeight: { xs: 480, md: "none" },
      }}
    >
      <ScrollTable stickyHeader containerSx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <TableHead>
          <TableRow sx={{ "& th": { bgcolor: "background.paper" } }}>
            <TableCell>Country</TableCell>
            <TableCell align="right">Companies</TableCell>
            <TableCell align="right">Website</TableCell>
            <TableCell align="right">Crawled</TableCell>
            <TableCell align="right">Classified</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {countries.map((c) => (
            <TableRow key={c.country} hover>
              <TableCell>
                <CountryName code={c.country} variant="body2" />
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatNumber(c.count)}
                {!!c.added_24h && (
                  <Typography variant="caption" component="div" sx={{ color: "success.main" }}>
                    +{formatNumber(c.added_24h)}
                  </Typography>
                )}
              </TableCell>
              <Share value={c.with_website} total={c.count} />
              <Share value={c.with_crawl} total={c.count} />
              <Share value={c.with_classification} total={c.count} />
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}

function Share({ value, total }: { value: number; total: number }) {
  const p = percentOf(value, total);
  return (
    <TableCell align="right" sx={{ width: 96 }}>
      <Tooltip title={`${formatNumber(value)} of ${formatNumber(total)}`}>
        <Box>
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {p}%
          </Typography>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: "action.hover", overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: `${p}%`, bgcolor: "primary.main", opacity: 0.7 }} />
          </Box>
        </Box>
      </Tooltip>
    </TableCell>
  );
}
